import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateTriggerProfile } from '@/lib/api/generated/api';
import type { TriggerProfileView } from '@/lib/api/generated/model';

import { toTriggerCreateError } from '../errors';
import { triggerKeys } from '../queryKeys';

/**
 * Enable or disable an existing trigger profile — the one editable field
 * after creation (FR-006, FR-013).
 *
 * Same convention as `useCreateTriggerProfile`: calls the generated raw
 * `updateTriggerProfile` fetcher inside a hand-written `useMutation` rather
 * than the generated `useUpdateTriggerProfile` hook, to narrow the
 * response's discriminated `status` union instead of asserting success.
 * `updateTriggerProfile` is the one PATCH endpoint shared with schedule
 * updates (backend Spec 065) — sending only `{ enabled }` selects the
 * `SetTriggerProfileEnabledRequest` branch of its `anyOf` body, never merged
 * with schedule fields.
 *
 * Invalidates both the list (US2's row) and the detail read (US3's sheet,
 * once it exists) — the same profile is visible in both places, and neither
 * should show a stale enabled state after the other changes it.
 */
export function useSetTriggerProfileEnabled(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      triggerProfileId,
      enabled,
    }: {
      triggerProfileId: string;
      enabled: boolean;
    }): Promise<TriggerProfileView> => {
      const response = await updateTriggerProfile(
        workspaceId,
        triggerProfileId,
        { enabled },
      );
      if (response.status === 200) return response.data;
      throw toTriggerCreateError(response.status, response.data);
    },
    onSuccess: (_profile, variables) => {
      void queryClient.invalidateQueries({
        queryKey: triggerKeys.list(workspaceId),
      });
      void queryClient.invalidateQueries({
        queryKey: triggerKeys.detail(workspaceId, variables.triggerProfileId),
      });
    },
  });
}
